import { marked, type Tokens } from 'marked';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import helpSource from '../../../docs/HELP.md?raw';

/**
 * In-app help (D-040). Renders docs/HELP.md — the same file the documentation contract keeps
 * current with every change — so there is one guide, and what users read is what shipped.
 * The file is bundled at build time; there is no runtime fetch and nothing to deploy separately.
 */

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/<[^>]+>/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

const renderer = new marked.Renderer();
renderer.heading = function heading({ tokens, depth }: Tokens.Heading): string {
  const text = this.parser.parseInline(tokens);
  return `<h${depth} id="${slugify(text)}">${text}</h${depth}>\n`;
};

export function renderHelp(source: string = helpSource): { html: string; toc: Array<{ id: string; text: string; depth: number }> } {
  const toc = marked
    .lexer(source)
    .filter((t): t is Tokens.Heading => t.type === 'heading' && (t.depth === 2 || t.depth === 3))
    .map((t) => ({ id: slugify(t.text), text: t.text, depth: t.depth }));
  const html = marked.parse(source, { gfm: true, renderer, async: false }) as string;
  return { html, toc };
}

export function Help() {
  const { hash } = useLocation();
  const { html, toc } = useMemo(() => renderHelp(), []);

  useEffect(() => {
    if (!hash) return;
    document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView({ block: 'start' });
  }, [hash, html]);

  return (
    <div className="help">
      <nav className="help__toc card" aria-label="Help contents">
        <div className="card__header">
          <div className="card__title">Contents</div>
        </div>
        <ul>
          {toc.map((h) => (
            <li key={h.id} className={h.depth === 3 ? 'help__toc-sub' : undefined}>
              <a href={`#${h.id}`}>{h.text}</a>
            </li>
          ))}
        </ul>
      </nav>
      {/* Our own Markdown, bundled at build time — not user content. */}
      <article className="help__body card" dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  );
}
